import { zodResolver } from '@hookform/resolvers/zod';
import { LoginRequest } from '@stayafrica/contracts';
import { Button, FormField, Input, Notice, OtpInput, PasswordInput } from '@stayafrica/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Lock, Mail, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthLayout } from '@/features/auth/AuthLayout';
import { authKeys, login, meQueryOptions, verifyMfa } from '@/features/auth/queries';
import { ApiRequestError } from '@/lib/api';
import { errorMessage } from '@/lib/errors';

const LoginSearch = z.object({
  redirect: z.string().optional(),
  reason: z.enum(['expired']).optional(),
});

export const Route = createFileRoute('/login')({
  validateSearch: LoginSearch,
  beforeLoad: async ({ context, search }) => {
    const user = await context.queryClient.ensureQueryData(meQueryOptions());
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router utilise une exception de redirection
    if (user) throw redirect({ to: search.redirect ?? '/' });
  },
  component: LoginPage,
});

const LoginForm = LoginRequest.pick({ email: true, password: true });
type LoginFormValues = z.infer<typeof LoginForm>;

function LoginPage() {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();

  const [step, setStep] = useState<'credentials' | 'mfa'>('credentials');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(LoginForm),
    defaultValues: { email: '', password: '' },
    mode: 'onBlur',
  });

  const goToApp = async () => {
    await queryClient.invalidateQueries({ queryKey: authKeys.me });
    await navigate({ to: search.redirect ?? '/' });
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    setSubmitting(true);
    try {
      const result = await login(values);
      if (result.status === 'MFA_REQUIRED') {
        setMfaToken(result.mfaToken);
        setStep('mfa');
        return;
      }
      queryClient.setQueryData(authKeys.me, result.user);
      await goToApp();
    } catch (error) {
      setFormError(loginErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  });

  function loginErrorMessage(error: unknown): string {
    if (error instanceof ApiRequestError) {
      if (error.code === 'INVALID_CREDENTIALS') return t('auth:errors.INVALID_CREDENTIALS');
      if (error.code === 'ACCOUNT_LOCKED') return t('auth:errors.ACCOUNT_LOCKED');
      if (error.code === 'ACCOUNT_DISABLED') return t('auth:errors.ACCOUNT_DISABLED');
      if (error.code === 'RATE_LIMITED') return t('auth:errors.RATE_LIMITED');
    }
    return errorMessage(error, t);
  }

  const submitMfa = async (code: string) => {
    if (!mfaToken || code.length !== 6) return;
    setFormError(null);
    setSubmitting(true);
    try {
      const result = await verifyMfa({ mfaToken, code });
      if (result.status === 'AUTHENTICATED') {
        queryClient.setQueryData(authKeys.me, result.user);
        await goToApp();
      }
    } catch (error) {
      setFormError(
        error instanceof ApiRequestError && error.code === 'INVALID_CREDENTIALS'
          ? t('auth:errors.MFA_INVALID')
          : loginErrorMessage(error),
      );
      setMfaCode('');
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'mfa') {
    return (
      <AuthLayout title={t('auth:mfa.title')} subtitle={t('auth:mfa.subtitle')}>
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void submitMfa(mfaCode);
          }}
          noValidate
        >
          {formError ? (
            <Notice tone="danger" role="alert">
              {formError}
            </Notice>
          ) : null}

          <div className="field">
            <span className="label">{t('auth:mfa.codeLabel')}</span>
            <OtpInput
              value={mfaCode}
              onChange={setMfaCode}
              onComplete={(code) => void submitMfa(code)}
              label={t('auth:mfa.codeLabel')}
              invalid={Boolean(formError)}
              disabled={submitting}
              autoFocus
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={submitting}
            disabled={mfaCode.length !== 6}
          >
            {t('auth:mfa.submit')}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStep('credentials');
              setMfaCode('');
              setFormError(null);
            }}
          >
            {t('auth:mfa.backToLogin')}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth:login.title')} subtitle={t('auth:login.subtitle')}>
      <form className="stack" onSubmit={(event) => void onSubmit(event)} noValidate>
        {search.reason === 'expired' && !formError ? (
          <Notice tone="info">{t('auth:login.sessionExpired')}</Notice>
        ) : null}

        {formError ? (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        ) : null}

        <FormField
          label={t('auth:login.email')}
          error={form.formState.errors.email ? t('common:validation.email') : undefined}
          required
          requiredLabel={t('common:fields.required')}
        >
          {(field) => (
            <Input
              id={field.id}
              type="email"
              autoComplete="username"
              placeholder={t('auth:login.emailPlaceholder')}
              icon={<Mail className="ico" aria-hidden="true" />}
              invalid={field.invalid}
              aria-describedby={field.describedBy}
              {...form.register('email')}
            />
          )}
        </FormField>

        <FormField
          label={
            <span className="row row--between" style={{ width: '100%' }}>
              <span>{t('auth:login.password')}</span>
              <Link to="/forgot-password" className="t-13">
                {t('auth:login.forgot')}
              </Link>
            </span>
          }
          error={form.formState.errors.password ? t('common:validation.required') : undefined}
          required
          requiredLabel={t('common:fields.required')}
        >
          {(field) => (
            <PasswordInput
              id={field.id}
              autoComplete="current-password"
              icon={<Lock className="ico" aria-hidden="true" />}
              invalid={field.invalid}
              aria-describedby={field.describedBy}
              showLabel={t('common:actions.showPassword')}
              hideLabel={t('common:actions.hidePassword')}
              {...form.register('password')}
            />
          )}
        </FormField>

        <Button type="submit" variant="primary" size="lg" block loading={submitting}>
          {t('auth:login.submit')}
          <ArrowRight className="ico ico-16" aria-hidden="true" />
        </Button>

        <Notice
          tone="violet"
          style={{ padding: '12px 14px' }}
          icon={<ShieldCheck className="ico ico-20 notice__ico" aria-hidden="true" />}
          title={<span className="t-13">{t('auth:login.mfaNoticeTitle')}</span>}
        >
          <p className="t-12 mb-0" style={{ opacity: 0.9 }}>
            {t('auth:login.mfaNoticeBody')}
          </p>
        </Notice>
      </form>
    </AuthLayout>
  );
}
